'use client';

import { Moon, Sun } from 'lucide-react';

import { useThemeToggle } from '@/hooks/use-theme-toggle';
import { cn, FOCUS_RING } from '@/lib/utils';

/**
 * Driven entirely by the `dark` class, never by `resolvedTheme`. The provider sets
 * `enableSystem={false}`, so next-themes reads storage in its state initialiser and there is no
 * post-mount state change — a component that renders from `resolvedTheme` keeps the server's light
 * output with no re-render to correct it. Deriving from CSS is also what kept the original icon
 * button honest, and it sidesteps Motion missing updates that land inside the view transition.
 */
export function ThemeToggleButton() {
  const { toggleTheme, isDark } = useThemeToggle();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggleTheme}
      // Hooks the view-transition rules in globals.css. Without them the thumb's travel is
      // invisible: the reveal circle reaches this corner last, so the old snapshot is still on
      // screen for the whole slide.
      data-theme-switch=""
      className={cn(
        // animate-ui's radix Switch, scaled up 20% and otherwise matched: their 20x32 track and
        // size-4 thumb become 24x38 and size-5, keeping the px-px inset, transparent border and
        // shadow-xs edge. The proportions are what make it read as the same control.
        'group relative flex h-6 w-[38px] flex-none cursor-pointer items-center rounded-full',
        // `--input`, the control-edge token: WCAG 1.4.11 puts a control's boundary at 3:1 against
        // the surface behind it, and an unlit track is a step off the bar it sits in. It also takes
        // the place of one pixel of the inset, so the thumb's travel is unchanged, and the focus
        // style colours this same border in.
        'border border-input px-px shadow-xs',
        'bg-secondary text-muted-foreground transition-colors duration-200',
        // Checked is only ever seen in dark, where `--primary` is the near-white ink — so the
        // track inverts against the bar instead of colliding with the `--card`/`--muted` surfaces.
        'dark:bg-primary dark:text-primary-foreground/70',
        // Pads the hit area out to the 44px touch floor on both axes without enlarging the control.
        "after:absolute after:top-1/2 after:left-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
        FOCUS_RING,
      )}
    >
      <span
        aria-hidden="true"
        data-theme-switch-thumb=""
        className={cn(
          'relative z-10 size-5 rounded-full bg-background dark:bg-primary-foreground',
          // The thumb is the state indicator, and in light it sits on a near-identical track, so it
          // needs the 3:1 edge WCAG 1.4.11 asks for. Dark has the contrast already.
          'border border-input dark:border-transparent',
          // 14px = the 38px track less its 2px border and 2px inset, less the 20px thumb.
          'translate-x-0 dark:translate-x-[14px]',
          // Anchored to whichever edge it rests on, so the press stretches inward rather than
          // pushing the thumb out through the end of the track.
          'group-active:w-[23px] dark:group-active:translate-x-[10px]',
          // `!` is load-bearing. The provider sets `disableTransitionOnChange`, so next-themes
          // injects `* { transition: none !important }` for exactly the frame the class flips —
          // the frame the thumb is supposed to slide on, which made it teleport. A class selector
          // outranks `*`, so an important rule here opts this one element out of the freeze.
          //
          // motion-safe for the same reason: the reduced-motion override in globals.css is also
          // `* { ... !important }`, and an unscoped important here would outrank that too.
          'motion-safe:transition-[translate,width]! motion-safe:duration-300! motion-safe:ease-[cubic-bezier(0.34,1.3,0.64,1)]!',
        )}
      />
      {/* Only the icon on the empty side shows — it names the mode the click would take you to. */}
      <span
        aria-hidden="true"
        className="absolute top-1/2 left-0.5 -translate-y-1/2 scale-0 opacity-0 motion-safe:transition-[scale,opacity]! motion-safe:duration-300! dark:scale-100 dark:opacity-100"
      >
        <Sun className="size-[11px]" />
      </span>
      <span
        aria-hidden="true"
        className="absolute top-1/2 right-0.5 -translate-y-1/2 scale-100 opacity-100 motion-safe:transition-[scale,opacity]! motion-safe:duration-300! dark:scale-0 dark:opacity-0"
      >
        <Moon className="size-[11px]" />
      </span>
      <span className="sr-only">Toggle theme</span>
    </button>
  );
}
